import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Upload, RefreshCw, ShieldCheck, AlertTriangle, ExternalLink, Search, X, Archive, CheckCircle2, Tag, FolderKanban, Download, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { PageHeader } from '@/components/ui/PageHeader'
import { CenterSpinner } from '@/components/ui/Spinner'
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  CountryBadge,
  MatchingScoreBadge,
  MatchingStatusBadge,
  PaymentStatusBadge,
  SourceBadge,
  WorkflowStatusBadge,
} from '@/components/ui/StatusBadge'
import { InvoiceFilters } from '@/components/invoices/InvoiceFilters'
import { UploadDialog } from '@/components/invoices/UploadDialog'
import { CashflowPanel } from '@/components/insights/CashflowPanel'
import { StatsStrip } from '@/components/ui/StatsStrip'
import { FileText, AlertCircle, Database, Clock } from 'lucide-react'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useAuditLogs, useBulkInvoiceAction, useInvoiceStats, useInvoices, useUploadInvoice } from '@/lib/api/hooks'
import { dateShort, dateTime, moneyCompact } from '@/lib/format'
import { hasPermission } from '@/lib/permissions'
import type { ListInvoicesParams } from '@/lib/api/types'
import { useToast } from '@/components/ui/ToastProvider'
import { api } from '@/lib/api/mockClient'
import { useQueryClient } from '@tanstack/react-query'

export function InvoicesPage() {
  const { activeCompanyId, activeRole } = useAuth()
  const nav = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()

  const [filters, setFilters] = useState<ListInvoicesParams>(() => ({
    companyId: activeCompanyId ?? '',
    page: 1,
    pageSize: 50,
  }))
  const [uploadOpen, setUploadOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const bulkAction = useBulkInvoiceAction()
  const uploadMut = useUploadInvoice()
  const [dragActive, setDragActive] = useState(false)
  const [dropProgress, setDropProgress] = useState<{ done: number; total: number } | null>(null)

  // Debounce 300 ms — gépelés közben ne nyomjuk a backendet minden karakternél.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, q: searchInput.trim() || undefined, page: 1 }))
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const effectiveParams = useMemo<ListInvoicesParams>(
    () => ({ ...filters, companyId: activeCompanyId ?? '' }),
    [filters, activeCompanyId],
  )

  const { data, isLoading, isFetching } = useInvoices(effectiveParams)
  const invoiceStats = useInvoiceStats(activeCompanyId)

  const canUpload = hasPermission(activeRole, 'invoices.upload')
  const canSync =
    hasPermission(activeRole, 'ingestion.navSync') || hasPermission(activeRole, 'ingestion.emailSync')

  async function triggerSync() {
    // Mindkét sync-et meghívjuk párhuzamosan és INDIVIDUAL toast-okat mutatunk a valós eredménnyel,
    // hogy ne tűnjön el a NAV kapcsolat-teszt eredménye az általános "Sync elindítva" üzenetben.
    const [navResult, emailResult] = await Promise.allSettled([
      api.triggerNavSync(),
      api.triggerEmailSync(),
    ])

    type Tone = 'success' | 'info' | 'warning' | 'error'
    const toneFor = (status: string | undefined): Tone =>
      status === 'ok' ? 'success' : status === 'error' ? 'error' : status === 'skipped' ? 'warning' : 'info'

    // NAV result
    if (navResult.status === 'fulfilled') {
      const r = navResult.value as { status?: string; detail?: string }
      toast.show({
        tone: toneFor(r.status),
        title: r.status === 'ok' ? 'NAV sync OK' : r.status === 'skipped' ? 'NAV sync kihagyva' : 'NAV sync',
        description: r.detail ?? 'NAV sync végrehajtva.',
      })
    } else {
      const msg = navResult.reason instanceof Error ? navResult.reason.message : 'NAV sync hiba'
      toast.show({ tone: 'error', title: 'NAV sync hiba', description: msg })
    }

    // Email result
    if (emailResult.status === 'fulfilled') {
      const r = emailResult.value as { status?: string; detail?: string }
      toast.show({
        tone: toneFor(r.status),
        title: r.status === 'ok' ? 'Email sync OK' : r.status === 'skipped' ? 'Email sync kihagyva' : 'Email sync',
        description: r.detail ?? 'IMAP letöltés végrehajtva.',
      })
    } else {
      const msg = emailResult.reason instanceof Error ? emailResult.reason.message : 'Email sync hiba'
      toast.show({ tone: 'error', title: 'Email sync hiba', description: msg })
    }

    qc.invalidateQueries({ queryKey: ['invoices'] })
  }

  if (!activeCompanyId) {
    return (
      <EmptyState
        title="Nincs aktív cég"
        description="Válassz egy céget a felül lévő cég-kapcsolóból a számlák megtekintéséhez."
      />
    )
  }

  const totalGross = data?.items.reduce((sum, i) => sum + (i.currency === 'HUF' ? i.grossAmount ?? 0 : 0), 0) ?? 0
  const stats = invoiceStats.data
  const navCount = stats?.bySource?.nav ?? 0
  const manualCount = stats?.bySource?.manual ?? 0
  const emailCount = stats?.bySource?.email ?? 0
  const szamlazzCount = stats?.bySource?.szamlazz ?? 0
  const pendingCount =
    (stats?.byWorkflowStatus?.received ?? 0) +
    (stats?.byWorkflowStatus?.under_review ?? 0) +
    (stats?.byWorkflowStatus?.discrepancy ?? 0)

  async function handleDroppedFiles(files: FileList | File[]) {
    if (!activeCompanyId) return
    if (!canUpload) {
      toast.show({ tone: 'error', title: 'Nincs jogosultság feltöltéshez' })
      return
    }
    const fileArray = Array.from(files).filter((f) => {
      const ok =
        f.type === 'application/pdf' ||
        f.type.startsWith('image/') ||
        /\.(pdf|jpg|jpeg|png|webp|xml)$/i.test(f.name)
      return ok
    })
    if (fileArray.length === 0) {
      toast.show({ tone: 'warning', title: 'Nincs feltölthető fájl', description: 'PDF / kép / XML kell.' })
      return
    }
    setDropProgress({ done: 0, total: fileArray.length })
    let ok = 0
    let fail = 0
    for (let i = 0; i < fileArray.length; i++) {
      try {
        await uploadMut.mutateAsync({ companyId: activeCompanyId, file: fileArray[i] })
        ok++
      } catch {
        fail++
      }
      setDropProgress({ done: i + 1, total: fileArray.length })
    }
    setDropProgress(null)
    toast.show({
      tone: fail === 0 ? 'success' : ok === 0 ? 'error' : 'warning',
      title: `${ok} feltöltve${fail > 0 ? `, ${fail} hibás` : ''}`,
    })
  }

  return (
    <div
      onDragEnter={(e) => {
        if (!canUpload) return
        e.preventDefault()
        if (Array.from(e.dataTransfer.types).includes('Files')) setDragActive(true)
      }}
      onDragOver={(e) => {
        if (!canUpload) return
        if (Array.from(e.dataTransfer.types).includes('Files')) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
        }
      }}
      onDragLeave={(e) => {
        // Csak ha tényleg elhagyjuk a container szélét, nem child elem váltáskor
        if (e.currentTarget === e.target) setDragActive(false)
      }}
      onDrop={(e) => {
        if (!canUpload) return
        e.preventDefault()
        setDragActive(false)
        if (e.dataTransfer.files.length > 0) void handleDroppedFiles(e.dataTransfer.files)
      }}
      className="relative"
    >
      {/* Drag-overlay */}
      {dragActive && canUpload && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-brand-600/10 backdrop-blur-sm pointer-events-none">
          <div className="rounded-xl border-2 border-dashed border-brand-500 bg-white dark:bg-slate-900 px-8 py-6 shadow-xl text-center">
            <Upload className="h-10 w-10 mx-auto text-brand-600 mb-2" />
            <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Engedd el a fájlokat itt
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              PDF / kép / XML — több fájl is mehet egyszerre
            </div>
          </div>
        </div>
      )}

      {/* Folyamatban — minimális progress chip */}
      {dropProgress && (
        <div className="fixed bottom-4 right-4 z-40 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg px-4 py-3 text-sm">
          <div className="font-medium">
            Feltöltés: {dropProgress.done} / {dropProgress.total}
          </div>
          <div className="mt-1 h-1.5 w-48 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div
              className="h-full bg-brand-600 transition-all"
              style={{ width: `${(dropProgress.done / dropProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}
      <PageHeader
        accent
        eyebrow="Pénzügyi modul"
        title="Számlák"
        subtitle={
          data
            ? `${data.meta.total} tétel · ${moneyCompact(totalGross)} bruttó (HUF tételek)`
            : 'Töltés…'
        }
        actions={
          <>
            {canSync && (
              <Button
                variant="secondary"
                onClick={triggerSync}
                leftIcon={<RefreshCw className="h-4 w-4" />}
              >
                Sync
              </Button>
            )}
            {canUpload && (
              <Button onClick={() => setUploadOpen(true)} leftIcon={<Upload className="h-4 w-4" />}>
                Feltöltés
              </Button>
            )}
          </>
        }
      />

      <StatsStrip
        tiles={[
          {
            label: 'Összesen',
            value: stats ? stats.total : '—',
            sub: stats ? `${pendingCount} feldolgozás alatt` : undefined,
            icon: <FileText className="h-3.5 w-3.5" />,
            tone: 'default',
          },
          {
            label: 'NAV-ról',
            value: navCount,
            sub: navCount > 0 ? `Aut. szinkron 6 óránként` : 'Még nincs',
            icon: <Database className="h-3.5 w-3.5" />,
            tone: navCount > 0 ? 'accent' : 'default',
            live: navCount > 0,
          },
          {
            label: 'Manuális + Email',
            value: manualCount + emailCount,
            sub: `${manualCount} kézi · ${emailCount} email · ${szamlazzCount} Számlázz.hu`,
            icon: <Clock className="h-3.5 w-3.5" />,
            tone: 'default',
          },
          {
            label: 'Bruttó (HUF)',
            value: moneyCompact(totalGross),
            sub: 'A betöltött oldal HUF tételei',
            icon: <AlertCircle className="h-3.5 w-3.5" />,
            tone: 'brand',
          },
        ]}
      />

      <NavSyncStatusPanel companyId={activeCompanyId} onSyncTriggered={() => qc.invalidateQueries({ queryKey: ['invoices'] })} />
      <CashflowPanel companyId={activeCompanyId} />

      {/* Kereső sáv — fulltext keresés invoice_number / supplier / project / kategória / notes mezőkön */}
      <div className="mb-3 relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <Search className="h-4 w-4" />
        </div>
        <Input
          type="text"
          placeholder="Keresés: számlaszám, beszállító, adószám, projekt, kategória, megjegyzés…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9 pr-9"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            aria-label="Keresés törlése"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {selected.size > 0 && (
        <BulkToolbar
          selected={selected}
          clear={() => setSelected(new Set())}
          loading={bulkAction.isPending}
          onAction={async (action, value) => {
            try {
              const res = await bulkAction.mutateAsync({ ids: Array.from(selected), action, value })
              toast.show({
                tone: res.fail === 0 ? 'success' : 'warning',
                title: `${res.ok} db sikeres${res.fail > 0 ? `, ${res.fail} hibás` : ''}`,
              })
              if (action === 'archive' || action === 'approve' || action === 'delete') setSelected(new Set())
            } catch (err) {
              toast.show({ tone: 'error', title: 'Bulk hiba', description: err instanceof Error ? err.message : String(err) })
            }
          }}
        />
      )}

      <InvoiceFilters
        value={effectiveParams}
        onChange={setFilters}
        onReset={() => setFilters({ companyId: activeCompanyId, page: 1, pageSize: 50 })}
      />

      {isLoading ? (
        <CenterSpinner />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          variant="invoices"
          title="Még nincs számla ebben a szűrésben"
          description="A NAV automatikusan 6 óránként szinkronizál, az email szintén. Manuálisan PDF/JPG/PNG-t is feltölthetsz közvetlenül."
          action={
            canUpload ? (
              <Button onClick={() => setUploadOpen(true)} leftIcon={<Upload className="h-4 w-4" />}>
                Számla feltöltése
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH className="w-8">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={(data?.items.length ?? 0) > 0 && data!.items.every((i) => selected.has(i.id))}
                  onChange={(e) => {
                    if (!data) return
                    const next = new Set(selected)
                    if (e.target.checked) data.items.forEach((i) => next.add(i.id))
                    else data.items.forEach((i) => next.delete(i.id))
                    setSelected(next)
                  }}
                  aria-label="Mind kijelölése"
                />
              </TH>
              <TH>Szállító</TH>
              <TH>Számlaszám</TH>
              <TH>Kelt</TH>
              <TH>Esedékes</TH>
              <TH align="right">Nettó</TH>
              <TH align="right">ÁFA</TH>
              <TH align="right">Bruttó</TH>
              <TH>Forrás</TH>
              <TH>Prezentálta</TH>
              <TH>Költség</TH>
              <TH>Ország</TH>
              <TH>Matching</TH>
              <TH>Workflow</TH>
              <TH>Fizetés</TH>
            </TR>
          </THead>
          <TBody>
            {data.items.map((i) => (
              <TR key={i.id} onClick={() => nav(`/invoices/${i.id}`)}>
                <TD onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5"
                    checked={selected.has(i.id)}
                    onChange={(e) => {
                      const next = new Set(selected)
                      if (e.target.checked) next.add(i.id)
                      else next.delete(i.id)
                      setSelected(next)
                    }}
                    aria-label="Kijelölés"
                  />
                </TD>
                <TD>
                  <div className="font-medium text-slate-900 dark:text-slate-100">{i.supplierName ?? '—'}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{i.supplierTaxNumber ?? '—'}</div>
                  {(i.tags?.length ?? 0) > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {i.tags!.slice(0, 3).map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 rounded-full bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200 px-1.5 py-0 text-[10px]"
                        >
                          {t}
                        </span>
                      ))}
                      {i.tags!.length > 3 && (
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">+{i.tags!.length - 3}</span>
                      )}
                    </div>
                  )}
                </TD>
                <TD className="font-mono text-xs">{i.invoiceNumber ?? '—'}</TD>
                <TD>{dateShort(i.issueDate)}</TD>
                <TD>{dateShort(i.paymentDueDate)}</TD>
                <TD numeric>{moneyCompact(i.netAmount, i.currency)}</TD>
                <TD numeric>{moneyCompact(i.vatAmount, i.currency)}</TD>
                <TD numeric className="font-semibold">
                  {moneyCompact(i.grossAmount, i.currency)}
                  {i.currency !== 'HUF' && i.grossAmountHuf != null && (
                    <div className="text-[10px] font-normal text-slate-500 dark:text-slate-400">
                      ≈ {moneyCompact(i.grossAmountHuf, 'HUF')}
                    </div>
                  )}
                </TD>
                <TD><SourceBadge source={i.source} /></TD>
                <TD>
                  {i.uploadedBy?.name ? (
                    <div className="text-xs">
                      <div className="font-medium text-slate-800 dark:text-slate-200">{i.uploadedBy.name}</div>
                      {i.createdAt && (
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">{dateShort(i.createdAt)}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </TD>
                <TD>
                  <div className="flex flex-col gap-0.5 text-[10px]">
                    {i.paidBy === 'personal' ? (
                      <span className="inline-flex items-center rounded bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 font-medium w-fit">
                        Magán{i.paidByUserName ? ` · ${i.paidByUserName}` : ''}
                      </span>
                    ) : i.paidBy === 'company' ? (
                      <span className="inline-flex items-center rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 px-1.5 py-0.5 font-medium w-fit">
                        Céges
                      </span>
                    ) : null}
                    {i.projectCode && (
                      <span className="inline-flex items-center rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-1.5 py-0.5 font-mono w-fit">
                        {i.projectCode}
                      </span>
                    )}
                    {(i.splits ?? []).slice(0, 3).map((s) => (
                      <span
                        key={s.id}
                        className="inline-flex items-center rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 px-1.5 py-0.5 font-medium w-fit"
                      >
                        {(s.userName ?? '?').slice(0, 1).toUpperCase()}
                        {Math.round(s.percent)}
                      </span>
                    ))}
                  </div>
                </TD>
                <TD><CountryBadge type={i.countryType} /></TD>
                <TD>
                  <div className="flex items-center gap-1 flex-wrap">
                    <MatchingStatusBadge status={i.matchingStatus} />
                    <MatchingScoreBadge score={i.matchingScore} />
                  </div>
                </TD>
                <TD><WorkflowStatusBadge status={i.workflowStatus} /></TD>
                <TD><PaymentStatusBadge status={i.paymentStatus} /></TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {isFetching && !isLoading && (
        <div className="mt-2 text-xs text-slate-400">Frissítés…</div>
      )}

      {data && data.meta.total > data.meta.pageSize && (
        <Pagination meta={data.meta} onPage={(p) => setFilters({ ...filters, page: p })} />
      )}

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} companyId={activeCompanyId} />
    </div>
  )
}

/**
 * Az InvoicesPage tetején állandóan látszó panel: a legutóbbi NAV sync mit csinált.
 * A user kérése: "ne legyen rejtve a log, kösd be a logok közé" — a backend-en a
 * `nav.sync_run` audit entry-ket kérjük le, és az elsőt megjelenítjük.
 *
 * Ezzel a NAV szinkron viselkedés (sikerült-e a kapcsolat-teszt, hány számla
 * érkezett, mennyi volt a kapcsolati hiba) NEM rejtődik a toast-mögé, hanem
 * a számla-lista fölött mindig olvasható, és az Audit log-ba közvetlen ugorhat.
 */
/**
 * Korábbi évek (pl. 2025) teljes letöltése a NAV-ból. A backend a 35-napos NAV
 * limitet 35-napos ablakokra bontva kezeli, így egy egész év is lekérhető egy gombbal.
 */
function NavYearBackfill({ onDone }: { onDone: () => void }) {
  const toast = useToast()
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i) // idei + 4 korábbi
  const [year, setYear] = useState(currentYear - 1) // alapból az előző teljes év
  const [busy, setBusy] = useState(false)

  async function run() {
    setBusy(true)
    try {
      const res = (await api.triggerNavSync({ from: `${year}-01-01`, to: `${year}-12-31` })) as {
        status?: string
        detail?: string
      }
      const tone =
        res.status === 'ok'
          ? 'success'
          : res.status === 'skipped'
            ? 'warning'
            : res.status === 'error'
              ? 'error'
              : 'info'
      toast.show({ tone, title: `NAV ${year} letöltés`, description: res.detail ?? 'Kész.' })
      onDone()
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'NAV év letöltés hiba',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-1" title="Egy teljes korábbi év számláinak letöltése a NAV-ból">
      <Select
        value={year}
        onChange={(e) => setYear(Number(e.target.value))}
        disabled={busy}
        className="h-8 w-[5.5rem] py-0 text-xs"
        aria-label="Év kiválasztása NAV letöltéshez"
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </Select>
      <Button size="sm" variant="secondary" onClick={run} loading={busy} leftIcon={<Download className="h-3.5 w-3.5" />}>
        Év letöltése
      </Button>
    </div>
  )
}

function NavSyncStatusPanel({ companyId, onSyncTriggered }: { companyId: string; onSyncTriggered: () => void }) {
  const { data: auditData } = useAuditLogs('nav_sync')
  const { data: stats } = useInvoiceStats(companyId)
  const toast = useToast()
  const [running, setRunning] = useState(false)
  const latest = auditData?.[0]

  const navCount = stats?.bySource?.nav ?? 0

  async function runNow() {
    setRunning(true)
    try {
      // V1.3+ ha elérhető cron job manual trigger használata (deterministic),
      // különben fallback a /jobs/nav-sync-ra (frontend kompatibilitás).
      const result = api.runCronJob
        ? await api.runCronJob('cron.nav-sync')
        : await api.triggerNavSync()
      const summary =
        typeof result === 'object' && result && 'summary' in result
          ? (result as { summary: string }).summary
          : 'NAV sync végrehajtva'
      toast.show({ tone: 'success', title: 'NAV sync futott', description: summary || 'OK' })
      onSyncTriggered()
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'NAV sync hiba',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    } finally {
      setRunning(false)
    }
  }

  // Üres állapot: még soha nem futott NAV sync ÉS nincs is letöltött NAV számla
  if (!latest && navCount === 0) {
    return (
      <div className="mb-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
        <span className="flex-1">
          NAV szinkron még nem futott. Kattints a <strong>Most futtass</strong> gombra a kapcsolat-tesztre, vagy
          állítsd be a kredenciálokat a{' '}
          <Link to="/settings/nav" className="underline text-brand-600 dark:text-brand-300">
            Beállítások → NAV
          </Link>{' '}
          alatt.
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          <NavYearBackfill onDone={onSyncTriggered} />
          <Button size="sm" variant="secondary" onClick={runNow} loading={running}>
            Most futtass
          </Button>
        </div>
      </div>
    )
  }

  const meta = (latest?.metadata ?? {}) as {
    companiesOk?: number
    companiesError?: number
    companiesTotal?: number
    invoicesDownloaded?: number
    note?: string
  }
  const lastRunOk = latest?.outcome === 'success'
  const lastRunNew = meta.invoicesDownloaded ?? 0
  const lastRunError = meta.companiesError ?? 0

  // Színlogika: piros csak akkor, ha tényleges hiba van a legutóbbi futásban
  const tone = lastRunError > 0 ? 'error' : navCount > 0 ? 'ok' : 'info'
  const colors: Record<string, string> = {
    ok: 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200',
    info: 'border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-950/30 text-sky-800 dark:text-sky-200',
    error: 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200',
  }
  const Icon = tone === 'error' ? AlertTriangle : ShieldCheck

  return (
    <div className={`mb-3 rounded-md border px-3 py-2 ${colors[tone]}`}>
      <div className="flex items-start gap-2 text-sm">
        <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium">
            <strong>{navCount}</strong> NAV számla rögzítve összesen
            {stats?.lastNavSyncAt && (
              <span className="ml-1 text-xs opacity-80">
                · utolsó letöltés: {dateTime(stats.lastNavSyncAt)}
              </span>
            )}
          </div>
          {latest && (
            <div className="text-xs mt-0.5 opacity-90">
              Legutóbbi sync futás: {dateTime(latest.createdAt)} —{' '}
              {lastRunOk
                ? `${meta.companiesOk ?? 0}/${meta.companiesTotal ?? 0} cég OK · ${lastRunNew} új számla ebben a körben`
                : `${lastRunError} hibás cég`}
            </div>
          )}
          {meta.note && lastRunNew === 0 && (
            <div className="text-xs mt-0.5 opacity-80">{meta.note}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <NavYearBackfill onDone={onSyncTriggered} />
          <Button size="sm" variant="ghost" onClick={runNow} loading={running}>
            Most futtass
          </Button>
          <Link
            to="/settings/audit"
            className="text-xs underline opacity-90 hover:opacity-100 flex items-center gap-1"
          >
            Audit log <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  )
}

function Pagination({
  meta,
  onPage,
}: {
  meta: { total: number; page: number; pageSize: number }
  onPage: (page: number) => void
}) {
  const totalPages = Math.ceil(meta.total / meta.pageSize)
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
      <span>
        {meta.page}. oldal a {totalPages}-ből · {meta.total} tétel
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={meta.page <= 1}
          onClick={() => onPage(meta.page - 1)}
        >
          Előző
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={meta.page >= totalPages}
          onClick={() => onPage(meta.page + 1)}
        >
          Következő
        </Button>
      </div>
    </div>
  )
}

/**
 * Bulk toolbar — megjelenik amikor van legalább 1 kijelölt számla.
 * Műveletek: tömeges címke / projekt-kód / kategória beállítás, archiválás, jóváhagyás.
 * A "Címke" / "Projekt" / "Kategória" gombok egy kis inline-promptot nyitnak az értékhez.
 */
function BulkToolbar({
  selected,
  clear,
  loading,
  onAction,
}: {
  selected: Set<string>
  clear: () => void
  loading: boolean
  onAction: (
    action: 'addTag' | 'setProjectCode' | 'setCategory' | 'archive' | 'approve' | 'delete',
    value?: string | null,
  ) => Promise<void>
}) {
  const [mode, setMode] = useState<null | 'addTag' | 'setProjectCode' | 'setCategory'>(null)
  const [value, setValue] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  async function submitValueAction() {
    if (!mode) return
    if (!value.trim()) return
    await onAction(mode, value.trim())
    setMode(null)
    setValue('')
  }

  return (
    <div className="mb-3 sticky top-0 z-10 rounded-lg border border-brand-200 dark:border-brand-700 bg-brand-50 dark:bg-brand-900/40 p-3 shadow-sm">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="font-medium text-brand-900 dark:text-brand-100">{selected.size} kijelölt</span>
        <span className="text-brand-700 dark:text-brand-300">·</span>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Tag className="h-3.5 w-3.5" />}
          onClick={() => {
            setMode('addTag')
            setValue('')
          }}
          disabled={loading}
        >
          Címke
        </Button>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<FolderKanban className="h-3.5 w-3.5" />}
          onClick={() => {
            setMode('setProjectCode')
            setValue('')
          }}
          disabled={loading}
        >
          Projekt
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setMode('setCategory')
            setValue('')
          }}
          disabled={loading}
        >
          Kategória
        </Button>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
          onClick={() => onAction('approve')}
          disabled={loading}
        >
          Jóváhagyás
        </Button>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Archive className="h-3.5 w-3.5" />}
          onClick={() => onAction('archive')}
          disabled={loading}
        >
          Archiválás
        </Button>
        <Button
          size="sm"
          variant="danger"
          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
          onClick={() => setConfirmDelete(true)}
          disabled={loading}
        >
          Törlés
        </Button>
        <div className="ml-auto" />
        <Button size="sm" variant="ghost" onClick={clear} leftIcon={<X className="h-3.5 w-3.5" />}>
          Kijelölés visszavonása
        </Button>
      </div>

      {confirmDelete && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm">
          <span className="text-red-800 dark:text-red-200">
            Biztosan törlöd a kijelölt <strong>{selected.size}</strong> számlát? (Visszavonható — soft delete, audit logba kerül.)
          </span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} disabled={loading}>
              Mégse
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={loading}
              leftIcon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={async () => {
                await onAction('delete')
                setConfirmDelete(false)
              }}
            >
              Törlés véglegesítése
            </Button>
          </div>
        </div>
      )}
      {mode && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={
              mode === 'addTag'
                ? 'Címke neve (pl. "számlák2025")'
                : mode === 'setProjectCode'
                  ? 'Projekt kód (üresen hagyva törlődik)'
                  : 'Kategória (üresen hagyva törlődik)'
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitValueAction()
              if (e.key === 'Escape') setMode(null)
            }}
            autoFocus
          />
          <Button size="sm" onClick={submitValueAction} loading={loading}>
            Alkalmaz {selected.size} sorra
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setMode(null)
              setValue('')
            }}
          >
            Mégse
          </Button>
        </div>
      )}
    </div>
  )
}
